import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Bookmark, BookmarkCheck, RotateCw, ArrowLeft, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { TID, CARD_CATEGORY_LABELS } from "@/lib/constants";
import { formatInline } from "@/lib/formatInline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/uploads/")) return `${BACKEND_URL}${u}`;
  return u;
}

/**
 * Double-tap detection thresholds.
 *   • DOUBLE_TAP_MS  — max time between the two taps (browsers commonly use 300ms).
 *   • DOUBLE_TAP_PX  — max distance between the two taps; anything larger is treated
 *     as two separate taps (prevents flip-flipping when the user is scrolling
 *     and lifts a finger twice on the vertical feed).
 */
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_PX = 30;

/**
 * Any click whose target matches this selector must NEVER trigger a flip —
 * it's an interactive control (bookmark button, flip/back button, source link,
 * form element) that owns its own click behaviour.
 */
const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, [role="button"], [data-no-flip]';

export default function FlipCard({ card, isBookmarked = false, onToggleBookmark }) {
  const [flipped, setFlipped] = useState(false);
  const [reduceMotion] = useState(() => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
  });

  /**
   * Double-tap-to-flip handler.
   *
   * Why a manual timestamp+coord tracker instead of `ondblclick`?
   *   • `ondblclick` is unreliable on iOS Safari (the OS may intercept the
   *     second tap for its own zoom gesture) and skips synthetic double-taps
   *     from most React-Native / TWA wrappers.
   *   • Tracking `event.clientX/Y` lets us reject a second tap that landed far
   *     from the first one, which is how we distinguish "double tap on the
   *     card" from "user was scrolling the vertical feed and their finger
   *     landed twice by accident".
   *
   * Guardrails (any one bails out silently):
   *   1. Click landed on an interactive element (button/link/form control).
   *   2. Click landed inside an active text selection (user is highlighting).
   *   3. The two taps were > DOUBLE_TAP_MS apart.
   *   4. The two taps were > DOUBLE_TAP_PX apart.
   */
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });

  const handleCardTap = (event) => {
    // 1. Ignore taps on interactive descendants — they own their own click.
    if (event.target && event.target.closest && event.target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }

    // 2. Ignore taps that occur while the user is selecting text on the card.
    try {
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.toString && sel.toString().length > 0) return;
    } catch { /* getSelection unavailable — ignore */ }

    const now = event.timeStamp || Date.now();
    const x = event.clientX ?? 0;
    const y = event.clientY ?? 0;

    const last = lastTapRef.current;
    const dt = now - last.time;
    const dx = Math.abs(x - last.x);
    const dy = Math.abs(y - last.y);

    if (dt > 0 && dt < DOUBLE_TAP_MS && dx < DOUBLE_TAP_PX && dy < DOUBLE_TAP_PX) {
      // Second tap qualifies — flip and reset the tracker so a third tap
      // isn't mistakenly paired with the second one.
      setFlipped((prev) => !prev);
      lastTapRef.current = { time: 0, x: 0, y: 0 };
      return;
    }

    // Otherwise this is the first tap of a possible pair — record it.
    lastTapRef.current = { time: now, x, y };
  };

  const badgeText = card.state_tag === "HPSC"
    ? "HPSC · State"
    : card.pib_tag
      ? "PIB Filtered"
      : (CARD_CATEGORY_LABELS[card.category] || card.category);

  const publishedAgo = (() => {
    try { return formatDistanceToNow(new Date(card.published_at), { addSuffix: true }); } catch { return "just now"; }
  })();

  const revisionAgo = (() => {
    if (!card.is_revision || !card.revision_read_at) return null;
    try { return formatDistanceToNow(new Date(card.revision_read_at), { addSuffix: true }); }
    catch { return null; }
  })();

  const imgSrc = resolveImg(card.image_url);
  const diagramSrc = resolveImg(card.diagram_url);

  return (
    <div
      className="w-full h-full max-w-[720px] mx-auto perspective-1200"
      data-testid={TID.cardRoot}
      data-card-id={card.card_id}
    >
      <motion.div
        className="relative preserve-3d w-full h-full"
        style={{ transformStyle: "preserve-3d" }}
        animate={reduceMotion ? {} : { rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.52, ease: "easeOut" }}
      >
        {/* FRONT — Inshorts-style single-screen layout */}
        <div
          aria-hidden={flipped ? "true" : "false"}
          onClick={handleCardTap}
          className={`${reduceMotion ? (flipped ? "hidden" : "") : "absolute inset-0"} ${flipped ? "pointer-events-none" : "pointer-events-auto"} backface-hidden bg-[#1A2232] border border-[#26334D] rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.35)] overflow-hidden flex flex-col cursor-pointer`}
          style={reduceMotion ? {} : { zIndex: flipped ? 1 : 2 }}
        >
          {/* Revision banner — visible only when card is served in revision mode */}
          {card.is_revision && (
            <div
              data-testid="card-revision-banner"
              className={`flex items-center justify-between gap-2 px-4 sm:px-5 py-2 border-b ${
                card.is_weak_spot
                  ? "bg-gradient-to-r from-[#B42318]/15 via-[#E85A2A]/10 to-transparent border-[#E85A2A]/30"
                  : "bg-gradient-to-r from-[#F2BD68]/15 via-[#F2BD68]/10 to-transparent border-[#F2BD68]/25"
              }`}
            >
              <span
                className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] ${
                  card.is_weak_spot ? "text-[#E85A2A]" : "text-[#F2BD68]"
                }`}
              >
                <RotateCw className="h-3 w-3" aria-hidden />
                {card.is_weak_spot ? "Weak Spot · Revise Now" : "Revision Mode"}
              </span>
              {revisionAgo && (
                <span
                  className={`text-[10.5px] truncate ${
                    card.is_weak_spot ? "text-[#E85A2A]/85" : "text-[#F2BD68]/80"
                  }`}
                >
                  Read {revisionAgo}
                </span>
              )}
            </div>
          )}
          {/* Header row: thumbnail + headline + category badge */}
          <div className="p-4 sm:p-5 pb-3 flex items-start gap-3 sm:gap-4">
            <div className="relative shrink-0">
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-[#26334D] ring-1 ring-inset ring-[#26334D]">
                {imgSrc && (
                  <img
                    src={imgSrc}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                {/* Harmonizing frame: subtle dark bottom fade + very light top vignette
                    so bright/light seed photos recess softly into the obsidian card. */}
                {imgSrc && (
                  <>
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(17,22,34,0.18) 0%, rgba(17,22,34,0) 45%, rgba(17,22,34,0.55) 100%)",
                      }}
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 rounded-xl"
                      style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)" }}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  data-testid={TID.cardCategoryBadge}
                  className="inline-flex items-center rounded-full bg-[#111622]/70 border border-[#F2BD68]/30 px-2.5 py-0.5 text-[10.5px] font-semibold text-[#F2BD68] tracking-wide uppercase"
                >
                  {badgeText}
                </span>
                {card.pyq_tag && (
                  <span data-testid={TID.cardPyq} className="inline-flex items-center rounded-full bg-[#2F6F4F]/15 border border-[#2F6F4F]/30 px-2 py-0.5 text-[10.5px] font-semibold text-[#7DE3B6] uppercase tracking-wide">PYQ · {card.pyq_tag}</span>
                )}
              </div>
              <h3
                data-testid={TID.cardHeadline}
                className="mt-2 font-semibold tracking-tight text-[#F8FAFC]"
                style={{ fontSize: "clamp(16px, 4.4vw, 20px)", lineHeight: 1.4 }}
              >
                {card.headline}
              </h3>
            </div>
          </div>

          {/* Divider */}
          <div className="mx-4 sm:mx-5 border-t border-[#26334D]" aria-hidden />

          {/* Summary body — scrollable on mobile so long paragraphs never hide behind the footer.
              Comfortable body scale (15-16px), generous line-height, and per-line spacing so
              bullet lists breathe without any custom bullet parsing. */}
          <div className="flex-1 min-h-0 px-4 sm:px-5 py-3 overflow-y-auto no-scrollbar">
            <p
              data-testid={TID.cardSummary}
              className="text-[#E2E8F0] font-normal break-words whitespace-pre-wrap [&>br]:mb-1"
              style={{
                fontSize: "clamp(15px, 4vw, 16px)",
                lineHeight: 1.75,
                letterSpacing: "0.005em",
              }}
            >
              {formatInline(card.summary_60)}
            </p>
          </div>

          {/* Footer: meta + actions — lighter text tone for legibility on the dark surface. */}
          <div className="px-4 sm:px-5 py-3 border-t border-[#26334D] bg-[#141B2B]">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11.5px] text-[#CBD5E1] min-w-0 truncate" style={{ lineHeight: 1.5 }}>
                <span>{publishedAgo}</span>
                {card.source ? (
                  <>
                    <span className="mx-1.5 text-[#64748B]">&middot;</span>
                    {card.source_url ? (
                      <a href={card.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline decoration-[#26334D] underline-offset-4 hover:text-[#E85A2A]">
                        {card.source}
                        <ExternalLink className="h-3 w-3" aria-hidden />
                      </a>
                    ) : (
                      <span className="underline decoration-[#26334D] underline-offset-4">{card.source}</span>
                    )}
                  </>
                ) : null}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  aria-label={isBookmarked ? "Remove bookmark" : "Bookmark this card"}
                  onClick={() => onToggleBookmark && onToggleBookmark(card, isBookmarked)}
                  data-testid={TID.cardBookmark}
                  className={`min-h-[44px] min-w-[44px] h-11 w-11 inline-flex items-center justify-center rounded-xl hover:bg-[#26334D] active:scale-95 transition-transform duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2BD68]/60 ${isBookmarked ? "text-[#F2BD68]" : "text-[#94A3B8]"}`}
                >
                  {isBookmarked ? <BookmarkCheck className="h-[22px] w-[22px]" /> : <Bookmark className="h-[22px] w-[22px]" />}
                </button>
                <button
                  type="button"
                  onClick={() => setFlipped((f) => !f)}
                  aria-label="Flip card to see exam utility"
                  data-testid={TID.cardFlip}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-5 py-2.5 min-h-[44px] text-sm font-semibold hover:bg-[#C94A1D] active:scale-95 transition-transform duration-150 shadow-[0_4px_12px_rgba(232,90,42,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2BD68]/70"
                >
                  <RotateCw className="h-[18px] w-[18px]" aria-hidden />
                  Flip
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* BACK — Prelims Pointer / Diagram / Mains Utility / Full Brief */}
        <div
          aria-hidden={flipped ? "false" : "true"}
          onClick={handleCardTap}
          className={`${reduceMotion ? (flipped ? "" : "hidden") : "absolute inset-0"} ${flipped ? "pointer-events-auto" : "pointer-events-none"} backface-hidden bg-[#1A2232] border border-[#26334D] rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.35)] overflow-hidden flex flex-col cursor-pointer`}
          style={reduceMotion ? {} : { transform: "rotateY(180deg)", zIndex: flipped ? 2 : 1 }}
        >
          <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-[#26334D] flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-semibold tracking-widest uppercase text-[#94A3B8]">Exam Utility</div>
              <h4 className="mt-0.5 text-sm font-semibold text-[#F8FAFC] break-words leading-snug">{card.headline}</h4>
            </div>
            <button
              type="button"
              onClick={() => setFlipped(false)}
              aria-label="Return to card front"
              data-testid={TID.cardFlipBack}
              className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2.5 min-h-[44px] text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] active:scale-95 transition-transform duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2BD68]/60"
            >
              <ArrowLeft className="h-[18px] w-[18px]" aria-hidden />
              Back
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 sm:px-5 py-4 space-y-5">
            {(card.prelims_pointers || []).length > 0 && (
              <div>
                <div className="text-[10.5px] font-semibold tracking-widest text-[#94A3B8] uppercase">📍 Prelims Pointer</div>
                <ul className="mt-2 space-y-2 text-sm text-[#F8FAFC]" style={{ lineHeight: 1.6 }}>
                  {card.prelims_pointers.map((p, i) => (
                    <li key={`${i}-${String(p).slice(0, 32)}`} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E85A2A]" /><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {diagramSrc && (
              <div>
                <div className="text-[10.5px] font-semibold tracking-widest text-[#94A3B8] uppercase">📊 Diagram</div>
                <div className="relative mt-2 overflow-hidden rounded-2xl border border-[#26334D] bg-[#111622]">
                  <img src={diagramSrc} alt="Diagram" className="w-full h-40 object-cover" loading="lazy" />
                  {/* Harmonizing frame — soft dark bottom fade + subtle top vignette */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(17,22,34,0.20) 0%, rgba(17,22,34,0) 40%, rgba(17,22,34,0.55) 100%)",
                    }}
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-2xl"
                    style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)" }}
                  />
                </div>
              </div>
            )}

            {card.mains_utility && (
              <div>
                <div className="text-[10.5px] font-semibold tracking-widest text-[#94A3B8] uppercase">📝 Mains Utility</div>
                <p className="mt-2 text-sm text-[#F8FAFC]" style={{ lineHeight: 1.6 }}>{card.mains_utility}</p>
              </div>
            )}

            {card.full_brief && (
              <div>
                <div className="text-[10.5px] font-semibold tracking-widest text-[#94A3B8] uppercase">📖 Full Brief</div>
                <p className="mt-2 text-sm text-[#F8FAFC] whitespace-pre-line" style={{ lineHeight: 1.7 }}>{formatInline(card.full_brief)}</p>
              </div>
            )}

            {(card.tags && card.tags.length > 0) && (
              <div className="pt-2">
                <div className="flex items-center flex-wrap gap-1.5">
                  {card.tags.slice(0, 6).map((t, i) => (
                    <span key={`${i}-${t}`} className="rounded-full border border-[#26334D] bg-[#1A2232] px-2 py-0.5 text-[10.5px] text-[#94A3B8]">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
